
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Key from 'plaid-threads/Icons/Key';
import Rocket from 'plaid-threads/Icons/Rocket';
import Sandbox from 'plaid-threads/Icons/Sandbox';
import Identity from 'plaid-threads/Icons/Identity';
import Help from 'plaid-threads/Icons/Help';
import Video from 'plaid-threads/Icons/Video';
import SampleCode from '../../../components/docs/plaidoms/SampleCode';
import DefinitionTable from '../../../components/docs/DefinitionTable';
import Layout from '../../../components/docs/Layout';
import TokenExchangeFlow from '../../../components/docs/plaidoms/TokenExchangeFlow';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
export const metadata = {
  toc: true,
  description: 'Follow these instructions to start building with Plaid. You will learn about our API keys, development environments, and more.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const RunQuickstart = makeShortcode("RunQuickstart");
const Image = makeShortcode("Image");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Header title='Get started with the Quickstart' subtitle='A quick introduction to building with Plaid' mdxType="Header" />
    <YouTubeEmbed videoId='U9xa1gzyPx8' alignment='full' title='Plaid in 3 Minutes' mdxType="YouTubeEmbed" />
    <Callout icon={<Video mdxType="Video" />} mdxType="Callout">
      <p>{`Want more video tutorials? The `}<a parentName="p" {...{
          "href": "https://www.youtube.com/watch?v=sGBvKDGgPjc"
        }}>{`full getting started guide`}</a>{` for the Quickstart app is available on YouTube.`}</p>
    </Callout>
    <Callout mdxType="Callout">
      <p>{`Don't want to write code? Check out the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/plaid-postman"
        }}>{`Plaid Postman Collection`}</a>{` for a no-code way to get started with Plaid's API.`}</p>
    </Callout>
    <Callout icon={<Identity mdxType="Identity" />} mdxType="Callout">
      <p>{`Looking to get started with Identity Verification, Income Verification, Plaid Check (CRA), or Layer? `}</p>
      <p>{`For Identity Verification, check out the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/idv-quickstart"
        }}>{`Identity Verification Quickstart`}</a>{`. `}</p>
      <p>{`For Plaid Check (CRA), check out the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/credit-quickstart"
        }}>{`Plaid Check (CRA) Quickstart`}</a>{`. `}</p>
      <p>{`For Income, try the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/income-sample"
        }}>{`Income Verification Starter app`}</a>{`.`}</p>
      <p>{`For Layer, use the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/layer-quickstart"
        }}>{`Layer Quickstart`}</a>{`.`}</p>
    </Callout>
    <h3 {...{
      "id": "introduction"
    }}>{`Introduction`}</h3>
    <Callout mdxType="Callout">
      <p>{`These instructions are also available on GitHub at the `}<a parentName="p" {...{
          "href": "https://github.com/plaid/quickstart"
        }}>{`Quickstart Repo`}</a>{`.`}</p>
    </Callout>
    <p>{`Let's test out running Plaid locally by cloning the Quickstart app. You'll need
API keys, which you can receive by signing up in the `}<a href="https://dashboard.plaid.com/developers/keys" target="_blank">{`Dashboard`}</a>{`.`}</p>
    <p>{`You'll have two different API keys, and there are two different Plaid
environments. Today we'll start in the Sandbox environment. View the API Keys
section of the Dashboard to find your Sandbox secret.`}</p>
    <DefinitionTable.Container tableName='API Key' linkText='View Keys in Dashboard' linkUrl='https://dashboard.plaid.com/developers/keys'>
  <DefinitionTable.Entry icon={<Key mdxType="Key" />} keyword='client_id'>
    Private identifier for your team
  </DefinitionTable.Entry>
  <DefinitionTable.Entry icon={<Key mdxType="Key" />} keyword='secret'>
    Private key, one for each of the two environments
  </DefinitionTable.Entry>
    </DefinitionTable.Container>
    <Layout.Spacer units={3} />
    <DefinitionTable.Container tableName='Environment'>
  <DefinitionTable.Entry icon={<Sandbox mdxType="Sandbox" />} keyword='Sandbox'>
    Get started with test credentials and life-like data
  </DefinitionTable.Entry>
  <DefinitionTable.Entry icon={<Rocket mdxType="Rocket" />} keyword='Production'>
    Test or launch your app with unlimited live credentials
  </DefinitionTable.Entry>
    </DefinitionTable.Container>
    <Layout.Spacer units={7} />
    <Callout icon={<Help mdxType="Help" />} mdxType="Callout">
      <p>{`If you get stuck at any point in the Quickstart, help is just a click away! Check the Quickstart `}<a parentName="p" {...{
          "href": "https://github.com/plaid/quickstart#troubleshooting"
        }}>{`troubleshooting guide`}</a>{`, ask `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new"
        }}>{`Bill`}</a>{` — Plaid's AI assistant — in the Dashboard, or chat with other developers in our `}<a parentName="p" {...{
          "href": "https://discord.gg/sf57M8DW3y"
        }}>{`Discord community`}</a>{`.`}</p>
    </Callout>
    <h3 {...{
      "id": "quickstart-setup"
    }}>{`Quickstart setup`}</h3>
    <p>{`Once you have your API keys, it's time to run the Plaid Quickstart locally! The instructions below will guide you through the process of cloning the `}<a href="https://github.com/plaid/quickstart" target="_blank">{`Quickstart repository`}</a>{`, customizing the .env file with your own Plaid client ID and Sandbox secret, and finally, building and running the app.`}</p>
    <Callout mdxType="Callout">
      <p>{`Make sure you have `}<a parentName="p" {...{
          "href": "https://docs.npmjs.com/downloading-and-installing-node-js-and-npm"
        }}>{`npm installed`}</a>{` before following along. If you're using Windows, ensure you have a terminal capable of running basic Unix shell commands.`}</p>
    </Callout>
    <ContentSwitcher.Container id='quickstart_setup_method' names={['CLI Setup', 'Manual Setup']}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-bash",
            "metastring": "switcher=false",
            "switcher": "false"
          }}>{`brew install plaid/plaid-cli/plaid
plaid register    # skip if you already have a Plaid account
plaid login
plaid samples create quickstart --language node   # or: python, ruby, go, java
`}</code></pre>
        <p>{`By default, this will configure the Quickstart to use the Auth, Balance, and Transactions products. You can change the configured products at any point by editing the `}<inlineCode parentName="p">{`.env`}</inlineCode>{` file in the Quickstart directory.`}</p>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <RunQuickstart product='auth,transactions' mdxType="RunQuickstart" />
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <p>{`Open a new shell and start the frontend app. Your app will be running at `}<inlineCode parentName="p">{`http://localhost:3000`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false # Run the Quickstart frontend",
        "switcher": "false",
        "#": true,
        "Run": true,
        "the": true,
        "Quickstart": true,
        "frontend": true
      }}>{`# Install dependencies
cd quickstart/frontend
npm install

# Start the frontend app
npm start

# Go to http://localhost:3000
`}</code></pre>
    <p>{`Visit localhost and log in with Sandbox credentials (typically `}<inlineCode parentName="p">{`user_good`}</inlineCode>{` and
`}<inlineCode parentName="p">{`pass_good`}</inlineCode>{`, as indicated at the bottom of the page).`}</p>
    <Image src='/assets/img/docs/quickstart/quickstart_screenshot.png' alt="Plaid Quickstart guide with 'Launch Link' button to simulate user bank account integration." width={500} mdxType="Image" />
    <Layout.Spacer units={2} />
    <h4 {...{
      "id": "create-your-first-item"
    }}>{`Create your first Item`}</h4>
    <p>{`Most API requests interact with an `}<em parentName="p">{`Item`}</em>{`, which is a Plaid term for a login at a financial institution. A single end-user of your application might have accounts at different financial institutions, which means they would have multiple different Items. An Item is not the same as a financial institution account, although every account will be associated with an Item. For example, if a user has one login at their bank that allows them to access both their checking account and their savings account, a single Item would be associated with both of those accounts.`}</p>
    <p>{`Now that you have the Quickstart running, you'll add your first Item in the Sandbox environment. Once you've opened the Quickstart app on localhost, click the `}<strong parentName="p">{`Launch Link`}</strong>{` button and select any institution. Use the Sandbox credentials to simulate a successful login.`}</p>
    <h5 {...{
      "id": "sandbox-credentials"
    }}>{`Sandbox credentials`}</h5>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "copy=false",
        "copy": "false"
      }}>{`username: user_good
password: pass_good
If prompted to enter a 2FA code: 1234
`}</code></pre>
    <p>{`Once you have entered your credentials and moved to the next screen, you have created your first Item! You can now make API calls for that Item by using the buttons in the Quickstart. In the next section, we'll explain what actually happened and how the Quickstart works.`}</p>
    <h3 {...{
      "id": "how-it-works"
    }}>{`How it works`}</h3>
    <p>{`As you might have noticed, you use both a server and a client-side component to
access the Plaid APIs. The flow looks like this:`}</p>
    <TokenExchangeFlow mdxType="TokenExchangeFlow" />
    <p>{`The first step is to create a new `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` by making a `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request and passing in the required configurations. This `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` is a short-lived, one-time use token that authenticates your app with `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Plaid Link`}</a>{`, our frontend module. Several of the environment variables you configured when launching the Quickstart, such as `}<inlineCode parentName="p">{`PLAID_PRODUCTS`}</inlineCode>{`, are used as parameters for the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`.`}</p>
    <SampleCode.CreateLinkToken product='auth' />
    <p>{`Once you have a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, you can use it to initialize `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link`}</a>{`. Link is a drop-in client-side module available for web, iOS, and Android that handles the authentication process. The Quickstart uses Plaid's optional React bindings for an integration that you trigger via your own client-side code. This is what your users use to log into their financial institution accounts.`}</p>
    <p>{`After a user submits their credentials within Link, Link provides you with a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` via the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. The code below shows how the Quickstart passes the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` from client-side code to the server. Both React and vanilla JavaScript examples are shown.`}</p>
    <MDXCodeBlock type='frontend' title="Initialize Link" mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-javascript"
        }}>{`<button id="link-button">Link Account</button>
<script src="https://cdnjs.cloudflare.com/ajax/libs/jquery/3.5.1/jquery.min.js"></script>
<script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
<script type="text/javascript">
(async function($) {
  var handler = Plaid.create({
    // Initialize Link with the link_token created by the server
    token: (await $.post('/create_link_token')).link_token,
    onLoad: function() {
      // Optional, called when Link loads
    },
    onSuccess: function(public_token, metadata) {
      // Send the public_token to your app server.
      // The metadata object contains info about the institution the
      // user selected and the account ID or IDs, if the
      // Account Select view is enabled.
      $.post('/exchange_public_token', {
        public_token: public_token,
      });
    },
    onExit: function(err, metadata) {
      // The user exited the Link flow.
      if (err != null) {
        // The user encountered a Plaid API error prior to exiting.
      }
      // metadata contains information about the institution
      // that the user selected and the most recent API request IDs.
      // Storing this information can be helpful for support.
    },
    onEvent: function(eventName, metadata) {
      // Optionally capture Link flow events, streamed through
      // this callback as your users connect an Item to Plaid.
      // For example:
      // eventName = "TRANSITION_VIEW"
      // metadata  = {
      //   link_session_id: "123-abc",
      //   mfa_type:        "questions",
      //   timestamp:       "2017-09-14T14:42:19.350Z",
      //   view_name:       "MFA",
      // }
    }
  });

  $('#link-button').on('click', function(e) {
    handler.open();
  });
})(jQuery);
</script>
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`// APP COMPONENT
// Upon rendering of App component, make a request to create and
// obtain a link token to be used in the Link component
import React, { useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
const App = () => {
  const [linkToken, setLinkToken] = useState(null);
  const generateToken = async () => {
    const response = await fetch('/api/create_link_token', {
      method: 'POST',
    });
    const data = await response.json();
    setLinkToken(data.link_token);
  };
  useEffect(() => {
    generateToken();
  }, []);
  return linkToken != null ? <Link linkToken={linkToken} /> : <></>;
};
// LINK COMPONENT
// Use Plaid Link and pass link token and onSuccess function
// in configuration to initialize Plaid Link
interface LinkProps {
  linkToken: string | null;
}
const Link: React.FC<LinkProps> = (props: LinkProps) => {
  const onSuccess = React.useCallback(async (public_token, metadata) => {
    // send public_token to server
    const response = await fetch('/api/set_access_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ public_token }),
    });
    // Handle response ...
  }, []);
  const config: Parameters<typeof usePlaidLink>[0] = {
    token: props.linkToken!,
    onSuccess,
  };
  const { open, ready } = usePlaidLink(config);
  return (
    <button onClick={() => open()} disabled={!ready}>
      Link account
    </button>
  );
};
export default App;
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Next, on the server side, the Quickstart calls `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` to obtain an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, as illustrated in the code excerpt below. The `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` uniquely identifies an Item and is a required argument for most Plaid API endpoints. In your own code, you'll need to securely store your `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` in order to make API requests for that Item.`}</p>
    <SampleCode.ExchangePublicToken />
    <h4 {...{
      "id": "making-api-requests"
    }}>{`Making API requests`}</h4>
    <p>{`Now that we've gone over the Link flow and token exchange process, we can explore what happens when you press a button in the Quickstart to make an API call. As an example, we'll look at the Quickstart's call to `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{`, which retrieves basic information, such as name and balance, about the accounts associated with an Item. The call is fairly straightforward and uses the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` as a single argument to the Plaid client object.`}</p>
    <MDXCodeBlock title="/accounts/get" type="server" mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`# Retrieve an Item's accounts
@app.route('/accounts', methods=['GET'])
def get_accounts():
  try:
      request = AccountsGetRequest(
          access_token=access_token
      )
      accounts_response = client.accounts_get(request)
  except plaid.ApiException as e:
      response = json.loads(e.body)
      return jsonify({'error': {'status_code': e.status, 'display_message':
                      response['error_message'], 'error_code': response['error_code'], 'error_type': response['error_type']}})
  return jsonify(accounts_response.to_dict())
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/accounts/get \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": String
  }'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`# Retrieve an Item's accounts
get '/accounts' do
  begin
    accounts_get_request = Plaid::AccountsGetRequest.new({ access_token: access_token })
    product_response = client.accounts_get(accounts_get_request)
    pretty_print_response(product_response.to_hash)
    content_type :json
    { accounts: product_response.accounts }.to_json
  rescue Plaid::ApiError => e
    error_response = format_error(e)
    pretty_print_response(error_response)
    content_type :json
    error_response.to_json
  end
end
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`app.get('/api/accounts', async function (request, response, next) {
  try {
    const accountsResponse = await client.accountsGet({
      access_token: accessToken,
    });
    prettyPrintResponse(accountsResponse);
    response.json(accountsResponse.data);
  } catch (error) {
    prettyPrintResponse(error);
    return response.json(formatError(error.response));
  }
});
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`func accounts(c *gin.Context) {
  accountsGetResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(
        *plaid.NewAccountsGetRequest(accessToken),
    ).Execute()

  if err != nil {
    c.JSON(http.StatusBadRequest, gin.H{"error": err.Error()})
    return
  }

  c.JSON(http.StatusOK, gin.H{
    "accounts": accountsGetResp.GetAccounts(),
  })
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`func accounts(c *gin.Context) {
    accountsGetResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{
        AccessToken: accessToken,
    })

    if err != nil {
        c.JSON(http.StatusBadRequest, gin.H{"error": err.Error()})
        return
    }

    c.JSON(http.StatusOK, gin.H{"accounts": accountsGetResp.Accounts})
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`@Path("/accounts")
@Produces(MediaType.APPLICATION_JSON)
public class AccountsResource {
  private PlaidApi plaidClient;

  public AccountsResource(PlaidApi plaidClient) {
    this.plaidClient = plaidClient;
  }

  @GET
  public AccountsGetResponse getAccounts() throws IOException {
    AccountsGetRequest request = new AccountsGetRequest()
      .accessToken(QuickstartApplication.accessToken);
    Response<AccountsGetResponse> accountsResponse = plaidClient
      .accountsGet(request)
      .execute();

    return accountsResponse.body();
  }
}
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Example response data:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#/accounts/get response",
        "#/accounts/get": true,
        "response": true
      }}>{`{
  "accounts": [
    {
      "account_id": "A3wenK5EQRfKlnxlBbVXtPw9gyazDWu1EdaZD",
      "balances": {
        "available": 100,
        "current": 110,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "0000",
      "name": "Plaid Checking",
      "official_name": "Plaid Gold Standard 0% Interest Checking",
      "subtype": "checking",
      "type": "depository"
    },
    {
      "account_id": "GPnpQdbD35uKdxndAwmbt6aRXryj4AC1yQqmd",
      "balances": {
        "available": 200,
        "current": 210,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "1111",
      "name": "Plaid Saving",
      "official_name": "Plaid Silver Standard 0.1% Interest Saving",
      "subtype": "savings",
      "type": "depository"
    },
    {
      "account_id": "nVRK5AmnpzFGv6LvpEoRivjk9p7N16F6wnZrX",
      "balances": {
        "available": null,
        "current": 1000,
        "iso_currency_code": "USD",
        "limit": null,
        "unofficial_currency_code": null
      },
      "mask": "2222",
      "name": "Plaid CD",
      "official_name": "Plaid Bronze Standard 0.2% Interest CD",
      "subtype": "cd",
      "type": "depository"
    },
    ...
  ],
  "item": {
    "available_products": [
      "assets",
      "balance",
      "identity",
      "investments",
      "transactions"
    ],
    "billed_products": ["auth"],
    "consent_expiration_time": null,
    "error": null,
    "institution_id": "ins_12",
    "item_id": "gVM8b7wWA5FEVkjVom3ri7oRXGG4mPIgNNrBy",
    "webhook": "https://requestb.in"
  },
  "request_id": "C3IZlexgvNTSukt"
}
`}</code></pre>
    <h4 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h4>
    <p>{`Congratulations, you have completed the Plaid Quickstart! From here, we invite you to modify the Quickstart code in order to get more practice with the Plaid API. There are a few directions you can go in now:`}</p>
    <p>{`Go to the `}<a parentName="p" {...{
        "href": "/docs"
      }}>{`docs homepage`}</a>{` for links to product-specific documentation.`}</p>
    <p>{`For more sample apps, including a bare-bones minimal Plaid Quickstart implementation and apps demonstrating real world examples of funds transfer and personal financial management, see `}<a parentName="p" {...{
        "href": "/docs/resources/#sample-apps"
      }}>{`sample apps`}</a>{`.`}</p>
    <p>{`Our YouTube playlist `}<a parentName="p" {...{
        "href": "https://www.youtube.com/playlist?list=PLyKH4ZiEQ1bE7XBcpX81BQWLy1olem1wf"
      }}>{`Plaid in 3 minutes`}</a>{` has brief introductions to many Plaid products. For more detailed tutorial videos, see `}<a parentName="p" {...{
        "href": "https://www.youtube.com/playlist?list=PLyKH4ZiEQ1bH5wpCt9SiyVfHlV2HecFBq"
      }}>{`Plaid Academy`}</a>{`.`}</p>
    <p>{`Looking to move money with a Plaid partner, such as `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships/dwolla"
      }}>{`Dwolla`}</a>{`? See `}<a parentName="p" {...{
        "href": "/docs/auth/partnerships/"
      }}>{`Move money with our partners`}</a>{` for partner-specific money movement Quickstarts.`}</p>
    <p>{`The Quickstart covers working with web apps. If your Plaid app will be on mobile, see `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Plaid Link`}</a>{` to learn about getting started with mobile client-side setup.`}</p>
    <Callout icon={<Help mdxType="Help" />} title="Need help?" mdxType="Callout">
      <p>{`If you'd like to integrate with Plaid and don't have in-house technical resources, `}<a parentName="p" {...{
          "href": "/docs/resources/#integration-support"
        }}>{`contact a Plaid integration partner`}</a>{` to build a Plaid integration for you.`}</p>
    </Callout>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "introduction",
        level: 3,
        title: "Introduction",
      },
{
        id: "quickstart-setup",
        level: 3,
        title: "Quickstart setup",
      },
{
        id: "create-your-first-item",
        level: 4,
        title: "Create your first Item",
      },
{
        id: "how-it-works",
        level: 3,
        title: "How it works",
      },
{
        id: "making-api-requests",
        level: 4,
        title: "Making API requests",
      },
{
        id: "next-steps",
        level: 4,
        title: "Next steps",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
